// Pure normalization for CluckUp usage events. Shared by the client beacon and
// the route handler so both agree on what a "view" is.
//
// Two cardinality rules drive everything here:
//   1. Dynamic segments collapse to route patterns — a per-document row per
//      view would make the tab-mix panel useless and would retain
//      content-identifying slugs.
//   2. Only allowlisted UI-state params become a `variant`. Everything else in
//      the query string is dropped.

export type UsageTab = "today" | "checks" | "cash" | "service" | "more" | "other";

const TAB_KEYS = new Set<UsageTab>(["today", "checks", "cash", "service", "more"]);

/** Params whose change counts as a new view. `outlet` is deliberately absent. */
export const USAGE_STATE_PARAMS = ["phase", "view", "date"] as const;

/** Known route patterns, longest-prefix first. */
const ROUTE_PATTERNS: readonly string[] = [
  "/cu/checks/history",
  "/cu/checks",
  "/cu/sops/[slug]",
  "/cu/sops",
  "/cu/today",
  "/cu/cash",
  "/cu/dsr",
  "/cu/service",
  "/cu/more",
  "/cu/eighty-six",
  "/cu/events",
  "/cu/maintenance",
  "/cu/recognition",
  "/cu/targets",
  "/cu/setup",
  "/cu/login",
  "/cu/pin",
  "/cu/welcome",
  "/cu",
];

export function normalizeUsagePath(pathname: string): string {
  const trimmed = pathname.replace(/\/+$/, "") || "/cu";
  if (trimmed.startsWith("/cu/sops/") && trimmed !== "/cu/sops") {
    return "/cu/sops/[slug]";
  }
  // Longest known prefix wins; anything deeper is clamped to it so an unknown
  // dynamic segment can never inflate cardinality.
  const match = ROUTE_PATTERNS.find(
    (pattern) => trimmed === pattern || trimmed.startsWith(`${pattern}/`),
  );
  return match ?? "/cu";
}

export function deriveTab(pathname: string): UsageTab {
  const segment = pathname.split("/")[2] ?? "";
  return TAB_KEYS.has(segment as UsageTab) ? (segment as UsageTab) : "other";
}

export function deriveVariant(
  pathname: string,
  params: URLSearchParams,
): string | null {
  const normalized = normalizeUsagePath(pathname);

  if (normalized === "/cu/checks/history") {
    return params.has("date") ? "history-browse" : null;
  }
  if (normalized === "/cu/checks") {
    const phase = params.get("phase");
    return phase === "open" || phase === "close" ? phase : null;
  }
  if (normalized === "/cu/service") {
    const view = params.get("view");
    return view === "incidents" || view === "guests" ? view : null;
  }
  return null;
}
